import React, {useEffect, useMemo, useState} from 'react';
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "@tanstack/react-query";
import {MaterialReactTable, MRT_ActionMenuItem, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import Papa from "papaparse";
import {IWarehouseProduct} from "../../../../../../utils/interfaces/my_warehouse/IWarehouseProduct";
import {
    getMuiTableBodyCellProps,
    getMuiTableBodyRowProps,
    getMuiTableHeadCellProps,
    getMuiTablePaperProps
} from "../../../../../ui/mui/mui_react_table/MuiReactTableStyles";
import Loading from "../../../../../ui/Loading";
import MuiFetchingSpinner from "../../../../../ui/mui/MuiFetchingSpinner";
import {Avatar, Box, Popover} from "@mui/material";
import {IWarehouseProductMovement} from "../../../../../../utils/interfaces/my_warehouse/IWarehouseProductMovement";
import {
    IMyCompanyWarehouse
} from "../../../../../../utils/interfaces/my_company/my_company_warehouse/IMyCompanyWarehouse";
import useApiGetWarehouseProductMovements from "./api-hooks/useApiGetWarehouseProductMovements";
import useMyWarehouseProductMovementCustomCellRenderer from "./useMyWarehouseProductMovementCustomCellRenderer";
import {formatDateTimeToDateString} from "../../../../../../logics/helpers";
import AddIcon from "@mui/icons-material/Add";
import MRTMuiButton from "../../../../../ui/mui/mui_react_table/MRTMuiButton";
import {Delete, Edit} from "@mui/icons-material";
import useApiDeleteManualWarehouseProductMovement from "./api-hooks/useApiDeleteManualWarehouseProductMovement";
import MuiAlertDialog from "../../../../../ui/mui/mui_modal/MuiAlertDialog";
import MyWarehouseManualProductMovementForm from "./MyWarehouseManualProductMovementForm";
import {GoArrowSwitch} from "react-icons/go";
import MuiReadOnlyBoxBody from "../../../../../ui/mui/mui-display-data/MuiReadOnlyBoxBody";
import {IWarehouseParentProduct} from "../../../../../../utils/interfaces/my_warehouse/IWarehouseParentProduct";
import RemoveIcon from '@mui/icons-material/Remove';


interface IObjCustomField extends IWarehouseProductMovement {
    custom_field: JSX.Element;
}

interface Props {
    warehouse: IMyCompanyWarehouse,
    product: IWarehouseProduct | IWarehouseParentProduct,
}


const MyWarehouseProductMovementsTable = ({warehouse, product}: Props) => {
    const theme = useTheme();
    const {t} = useTranslation()
    const queryClient = useQueryClient();

    const [selectedRow, setSelectedRow] = useState<IWarehouseProductMovement | undefined>()
    const [rows, setRows] = useState<IWarehouseProductMovement[] | undefined>()
    const [movementSign, setMovementSign] = useState<'positive' | 'negative'>('positive')

    const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false)

    const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const deleteMutation = useApiDeleteManualWarehouseProductMovement(warehouse.id, product.id, selectedRow?.id);
    const {mutateAsync: apiDelete} = deleteMutation;

    const handleConfirmDelete = async () => {
        try {
            await apiDelete({pk: selectedRow?.id});
        } catch (error) {

        }
        setDeleteConfirmationOpen(false);
    };

    const handleCancelDelete = () => {
        setDeleteConfirmationOpen(false);
    };


    const {
        data,
        isLoading,
        isFetching,
    } = useApiGetWarehouseProductMovements(warehouse.id, product.id);


    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);

    const CustomCellRenderer: React.FC<{ row: IWarehouseProductMovement, theme: any }> = ({row, theme}) => {
        const {renderIcons} = useMyWarehouseProductMovementCustomCellRenderer(row, theme);
        return renderIcons();
    };


    const [objsWithCustomField, setObjsWithCustomField] = useState<IObjCustomField[] | undefined>();
    useEffect(() => {
        const dataWithCustomField: IObjCustomField[] | undefined = rows?.map((row) => ({
            ...row,
            custom_field: <CustomCellRenderer row={row} theme={theme}/>
        }));
        setObjsWithCustomField(dataWithCustomField);
    }, [rows, theme]);


    const convertArrayOfObjectsToCSV = (data: IWarehouseProductMovement[]): string => {
        // Create new array of objects without the unnecessary fields
        const reducedData = data.map(({...rest}) => rest);

        // Convert to CSV
        return Papa.unparse(reducedData, {
            delimiter: ';',
        });
    };


    const columns = useMemo<MRT_ColumnDef<IWarehouseProductMovement>[]>(
        () => [
            {
                accessorKey: 'custom_field',
                header: '',
                enableColumnActions: false,
                enableColumnFilter: false,
                enableColumnOrdering: false,
                enableSorting: false,
            },
            {
                accessorKey: 'movement_date',
                header: t('movement_date'),
                accessorFn: (row) => formatDateTimeToDateString(row.movement_date),
                enableColumnFilter: false
            },
            {
                // accessorKey: 'quantity',
                header: t('load'),
                accessorFn: (row) => row.quantity >= 0 ? row.quantity.toFixed(2) : '',
                enableSorting: false,
                enableColumnFilter: false
            },
            {
                // accessorKey: 'quantity',
                header: t('download'),
                accessorFn: (row) => row.quantity < 0 ? row.quantity.toFixed(2) : '',
                enableSorting: false,
                enableColumnFilter: false
            },
            // {
            //     accessorKey: 'um',
            //     header: t('um'),
            // },
            {
                accessorKey: 'description',
                header: t('description'),
                filterFn: 'contains',
            },
        ],
        [rows]
    );


    const table = useMaterialReactTable<IWarehouseProductMovement>({
        columns,
        defaultColumn: {
            maxSize: 400,
            minSize: 80,
            size: 80,
        },
        data: objsWithCustomField ? objsWithCustomField : [],
        columnFilterDisplayMode: 'popover',
        globalFilterFn: 'contains',
        enableFullScreenToggle: false,
        enableFilters: true,
        enableDensityToggle: true,
        muiTableHeadCellProps: getMuiTableHeadCellProps(theme.palette.primary.main),
        muiTableBodyCellProps: getMuiTableBodyCellProps(theme),
        muiTablePaperProps: getMuiTablePaperProps,
        enablePagination: objsWithCustomField && objsWithCustomField.length > 10,
        initialState: {
            density: 'compact',
            pagination: {pageSize: 25, pageIndex: 0},
        },
        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.backgroundAppGarageColor.main,

        }),
        muiTableBodyRowProps: getMuiTableBodyRowProps(theme),

        renderTopToolbarCustomActions: ({table}) => (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <>
                    <Avatar sx={{
                        backgroundColor: 'white',
                        border: `2px solid ${theme.palette.primary.main}`,
                        marginRight: 1
                    }}>
                        <GoArrowSwitch color={theme.palette.primary.main}/>
                    </Avatar>
                    <MuiReadOnlyBoxBody label={''} value={'movements'}/>
                    <MRTMuiButton
                        icon={<AddIcon style={{color: theme.palette.primary.main, fontSize: '2rem'}}/>}
                        onClick={() => {
                            setMovementSign('positive')
                            setSelectedRow(undefined);
                            setIsEditFormOpen(true)
                        }} label={'add load'}/>
                    <MRTMuiButton
                        icon={<RemoveIcon style={{color: theme.palette.primary.main, fontSize: '2rem'}}/>}
                        onClick={() => {
                            setMovementSign('negative')
                            setSelectedRow(undefined);
                            setIsEditFormOpen(true)
                        }} label={'add download'}/>
                    {/*{rows &&*/}
                    {/*    <CSVLink data={convertArrayOfObjectsToCSV(rows)} filename={'all_rows.csv'}>*/}
                    {/*        {<FileDownloadIcon*/}
                    {/*            style={{color: theme.palette.primary.main, fontSize: '2rem', marginTop: 8}}/>}*/}
                    {/*    </CSVLink>*/}
                    {/*}*/}

                </>
            </Box>),

        enableRowActions: true,
        renderRowActionMenuItems: ({closeMenu, row}) => [
            <MRT_ActionMenuItem
                icon={<Edit color="primary"/>}
                key="edit"
                label={t("edit")}
                onClick={(event) => {
                    setSelectedRow(row.original);
                    setIsEditFormOpen(true)
                    closeMenu()
                }}
                table={table}
                disabled={!row.original.is_manual}
            />,
            <MRT_ActionMenuItem
                icon={<Delete color="warning"/>}
                key="delete"
                label={t("delete")}
                onClick={() => {
                    console.log(row.original)
                    setSelectedRow(row.original);
                    setDeleteConfirmationOpen(true)
                    closeMenu()
                }}
                table={table}
                disabled={!row.original.is_manual}
            />,
        ],
    });


    return <>
        {isLoading ? <Loading/> :
            <>

                <MaterialReactTable table={table}/>

                <MuiAlertDialog
                    open={isDeleteConfirmationOpen}
                    title="delete_confirmation"
                    content="ask_for_delete"
                    onClose={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />

                <Popover
                    id={'edit_popover'}
                    open={isEditFormOpen}
                    onClose={() => setIsEditFormOpen(false)}
                    anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                    }}
                >
                    <MyWarehouseManualProductMovementForm
                        objToEdit={selectedRow ? selectedRow : null}
                        onClose={() => setIsEditFormOpen(false)}
                        warehouse={warehouse}
                        product={product}
                        movement_sign={movementSign}
                    />
                </Popover>


                {isFetching &&
                    <MuiFetchingSpinner text="Fetching data..."/>
                }
            </>}
    </>;
};

export default MyWarehouseProductMovementsTable;
